/**
 * `/login`
 *
 * Structure transcribed from the mockup's `isAuth` block. The copy bound to `authKicker`,
 * `authHeading` and `authBlurb` sits in the mockup's trailing script, which exceeds the design
 * tool's fetch cap — so those strings are written here in the mockup's voice rather than copied.
 *
 * The server returns one message for every authentication failure — unknown address and wrong
 * password are indistinguishable — and this screen must not undo that by inferring anything more
 * specific. Whatever the server said is what the user sees.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';

import { AuthCard, AuthLayout } from '../../components/composites/AuthLayout';
import { Button, Field, Notice } from '../../components/primitives';
import { ApiError, signIn } from './api';
import { ME_QUERY_KEY, useAuth } from './useAuth';

export function SignInScreen(): React.JSX.Element {
  const { t } = useTranslation('common');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = useSearch({ strict: false }) as { redirect?: string };
  const { account, isLoading } = useAuth();

  useEffect(() => {
    if (!isLoading && account) {
      const target =
        search.redirect && search.redirect.startsWith('/') ? search.redirect : '/liturgies';
      navigate({ to: target as never, replace: true });
    }
  }, [account, isLoading, search.redirect, navigate]);

  const mutation = useMutation({
    mutationFn: () => signIn({ email, password, remember_me: rememberMe }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      const target =
        search.redirect && search.redirect.startsWith('/') ? search.redirect : '/liturgies';
      await navigate({ to: target as never, replace: true });
    },
  });

  const error = mutation.error instanceof ApiError ? mutation.error : undefined;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };

  return (
    <AuthLayout
      eyebrow="PIUS SELECTIONS"
      heading={t('auth.signin.title')}
      blurb={t('auth.signin.lede')}
    >
      {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

      <form onSubmit={submit} noValidate>
        <AuthCard>
          <Field
            label={t('auth.signin.email')}
            type="email"
            name="email"
            placeholder="name@parish.org"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />

          <Field
            label={t('auth.signin.password')}
            type="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />

          <div className="auth-options">
            <label className="auth-options__remember">
              <input
                type="checkbox"
                name="remember_me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              {t('auth.signin.remember_me')}
            </label>

            <Link to="/password-reset">{t('auth.signin.forgot_password')}</Link>
          </div>

          <Button type="submit" variant="primary" disabled={mutation.isPending}>
            {mutation.isPending ? t('auth.signin.submitting') : t('auth.signin.submit')}
          </Button>
        </AuthCard>
      </form>

      <p className="auth-alt">
        {t('auth.signin.no_account')}{' '}
        <Link to="/register" search={search.redirect ? { redirect: search.redirect } : {}}>
          {t('auth.signin.register_link')}
        </Link>
      </p>
    </AuthLayout>
  );
}
